/**
 * DatabricksPanel Component
 * Displays real-time analytics data from Databricks
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  getDatabricksStatus,
  getMarketPrices,
  getOperatorMetrics,
  getProductionSummary
} from '../services/databricksClientService';
import { isWaterTenant, isEmissionsTenant } from '../config/gisConfig';
import '../styles/DatabricksPanel.css';

// Both water and emissions schemas share the same column shape
// (volume_bbl, disposal_vol_bbl, recycled_vol_bbl, etc) but with
// different real-world meanings. UI labels swap per tenant.
const isNexusShape = isWaterTenant || isEmissionsTenant;
const TENANT_LABELS = isEmissionsTenant ? {
  marketSection:    'Methane / Carbon Markets',
  marketCardHead:   'Latest Settlement',
  priceUnit:        '/tCO₂e',
  changeLabel:      'vs last month',
  operatorsTitle:   'Top Operators by CH₄ Mitigated',
  operatorPrimary:  (op) => `${formatN(op.total_volume_bbl)} kg`,
  operatorSecond:   (op, fmt) => `🛠 ${fmt(op.recycling_rate_pct)}% repair rate`,
  operatorIcon:     '🛠',
  productionTitle:  'CH₄ Mitigation by District (30-day)',
  productionCols:   { county: 'District', total: 'Detected (kg)', disposed: 'Repaired (kg)', recycled: 'Pending (kg)' },
} : isWaterTenant ? {
  marketSection:    'Water Market Rates',
  marketCardHead:   'Recycling Rate',
  priceUnit:        '/bbl',
  changeLabel:      'vs last month',
  operatorsTitle:   'Top Operators by Volume',
  operatorPrimary:  (op) => `${formatN(op.total_volume_bbl)} bbl`,
  operatorSecond:   (op, fmt) => `♻️ ${fmt(op.recycling_rate_pct)}%`,
  operatorIcon:     '♻️',
  productionTitle:  'Water Volume by County (30-day)',
  productionCols:   { county: 'County', total: 'Total (bbl)', disposed: 'Disposed (bbl)', recycled: 'Recycled (bbl)' },
} : null;

function formatN(v) {
  if (v == null || isNaN(parseFloat(v))) return '--';
  const n = parseFloat(v);
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toFixed(0);
}

function DatabricksPanel({ isOpen, onToggle, selectedWell, analyticsData, queryResults }) {
  const [status, setStatus] = useState(null);
  const [marketPrices, setMarketPrices] = useState(null);
  const [operators, setOperators] = useState([]);
  const [productionSummary, setProductionSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [currentContext, setCurrentContext] = useState({ county: null, operator: null });

  // Extract context from GIS query results (most reliable) or analytics query
  const extractContext = useCallback((features, query) => {
    let county = null;
    let operator = null;

    // First: Extract from actual GIS feature data (most reliable)
    if (features && features.length > 0) {
      const counties = new Set();
      const operators = new Set();

      features.forEach(feature => {
        const props = feature.properties || feature.attributes || {};
        // Try different county field names
        const featureCounty = props.County || props.county || props.CountyName || props.COUNTY;
        if (featureCounty) {
          counties.add(featureCounty.toUpperCase());
        }
        // Try different operator field names
        const featureOperator = props.operator || props.Operator || props.OPERATOR;
        if (featureOperator) {
          operators.add(featureOperator);
        }
      });

      // If all features are from ONE county, use that as filter
      if (counties.size === 1) {
        county = Array.from(counties)[0];
      }
      // If all features have ONE operator, use that as filter
      if (operators.size === 1) {
        operator = Array.from(operators)[0];
      }

      console.log('📊 Databricks context from features:', { counties: Array.from(counties), operators: Array.from(operators), filtered: { county, operator } });
    }

    // Fallback: Parse from query string if no feature-based context
    if (!county && query) {
      const queryLower = query.toLowerCase();
      const countyMatch = queryLower.match(/\b(borden|crane|culberson|dawson|ector|glasscock|howard|lea|loving|martin|midland|pecos|reagan|reeves|upton|ward|winkler)\b/i);
      county = countyMatch ? countyMatch[1].toUpperCase() : null;
    }

    if (!operator && query) {
      const queryLower = query.toLowerCase();
      const operatorMatch = queryLower.match(/\b(diamondback|eog|occidental|mewbourne|devon|conocophillips|exxon|coterra|apache|pioneer)\b/i);
      operator = operatorMatch ? operatorMatch[1] : null;
    }

    return { county, operator };
  }, []);

  // Fetch all data with context filters
  const fetchData = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);

    try {
      console.log('📊 Fetching Databricks data with filters:', filters);

      // Fetch all data in parallel with filters
      const [statusRes, pricesRes, operatorsRes, summaryRes] = await Promise.all([
        getDatabricksStatus(),
        getMarketPrices(),
        getOperatorMetrics(filters),
        getProductionSummary(filters)
      ]);

      setStatus(statusRes);
      setMarketPrices(pricesRes);
      setOperators(operatorsRes.data || []);
      setProductionSummary(summaryRes.data || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch Databricks data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Update context when queryResults or analyticsData changes
  useEffect(() => {
    // Get features from queryResults
    const features = queryResults?.features || [];
    const query = analyticsData?.query || '';

    const newContext = extractContext(features, query);

    if (newContext.county !== currentContext.county || newContext.operator !== currentContext.operator) {
      console.log('🎯 Databricks context changed:', newContext);
      setCurrentContext(newContext);
      // Re-fetch data with new context if panel is open
      if (isOpen) {
        fetchData(newContext);
      }
    }
  }, [queryResults, analyticsData, extractContext, currentContext, isOpen, fetchData]);

  // Initial data fetch when panel opens
  useEffect(() => {
    if (isOpen && !lastRefresh) {
      fetchData(currentContext);
    }
  }, [isOpen, lastRefresh, fetchData, currentContext]);

  // Format price change with color
  const formatPriceChange = (change, prefix = '') => {
    const num = parseFloat(change);
    if (isNaN(num)) return null;
    const isPositive = num > 0;
    const color = isPositive ? '#4caf50' : '#f44336';
    const arrow = isPositive ? '▲' : '▼';
    return (
      <span style={{ color, fontSize: '0.85rem' }}>
        {arrow} {prefix}{Math.abs(num).toFixed(2)}%
      </span>
    );
  };

  // Format number with commas
  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    const parsed = typeof num === 'string' ? parseFloat(num) : num;
    return isNaN(parsed) ? 'N/A' : parsed.toLocaleString();
  };

  // Safely format price (handles strings from Databricks)
  const formatPrice = (value, decimals = 2) => {
    if (value === null || value === undefined) return '--';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? '--' : num.toFixed(decimals);
  };

  // Only show the toggle button when analytics data exists (after "summarize")
  if (!isOpen) {
    // Don't show anything until user asks for analytics summary
    if (!analyticsData) {
      return null;
    }
    return (
      <button className="databricks-toggle" onClick={onToggle}>
        <span className="databricks-icon">⚡</span>
        <span>Databricks</span>
      </button>
    );
  }

  return (
    <div className="databricks-panel">
      <div className="databricks-header">
        <div className="databricks-title">
          <span className="databricks-icon">⚡</span>
          <h3>Databricks Analytics</h3>
        </div>
        <div className="databricks-header-actions">
          {status?.connected && (
            <span className="connection-badge connected">
              <span className="status-dot"></span>
              Connected
            </span>
          )}
          {!status?.connected && status && (
            <span className="connection-badge demo">
              <span className="status-dot"></span>
              {status.mode === 'demo' ? 'Demo Mode' : 'Disconnected'}
            </span>
          )}
          <button
            className="refresh-button"
            onClick={() => fetchData(currentContext)}
            disabled={loading}
            title="Refresh data"
          >
            {loading ? '⟳' : '↻'}
          </button>
          <button className="close-button" onClick={onToggle}>×</button>
        </div>
      </div>

      {/* Context Badge - shows what data is filtered to */}
      {(currentContext.county || currentContext.operator) && (
        <div className="databricks-context-badge">
          <span className="context-label">Showing data for:</span>
          {currentContext.county && (
            <span className="context-tag county">📍 {currentContext.county} County</span>
          )}
          {currentContext.operator && (
            <span className="context-tag operator">🏢 {currentContext.operator}</span>
          )}
        </div>
      )}

      {error && (
        <div className="databricks-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="databricks-content">
        {/* Market Prices Section */}
        <section className="databricks-section">
          <h4>{TENANT_LABELS ? TENANT_LABELS.marketSection : 'Commodity Prices'}</h4>
          <div className="price-cards">
            {isNexusShape ? <>
              <div className="price-card">
                <div className="price-label">{TENANT_LABELS.marketCardHead}</div>
                <div className="price-value">
                  ${formatPrice(marketPrices?.data?.current?.price_usd)}
                </div>
                <span className="price-sublabel">${TENANT_LABELS.priceUnit}</span>
              </div>
              <div className="price-card">
                <div className="price-label">30-Day Avg</div>
                <div className="price-value">
                  ${formatPrice(marketPrices?.data?.current?.price_30d_avg)}
                </div>
                <span className="price-sublabel">${TENANT_LABELS.priceUnit}</span>
              </div>
              <div className="price-card">
                <div className="price-label">90-Day Avg</div>
                <div className="price-value">
                  ${formatPrice(marketPrices?.data?.current?.price_90d_avg)}
                </div>
                <span className="price-sublabel">${TENANT_LABELS.priceUnit}</span>
              </div>
              <div className="price-card">
                <div className="price-label">30d Change</div>
                <div className="price-value" style={{color: parseFloat(marketPrices?.data?.current?.pct_change_30d) >= 0 ? '#16a34a' : '#dc2626'}}>
                  {marketPrices?.data?.current?.pct_change_30d ? `${parseFloat(marketPrices.data.current.pct_change_30d) >= 0 ? '+' : ''}${formatPrice(marketPrices.data.current.pct_change_30d)}%` : '--'}
                </div>
                <span className="price-sublabel">{TENANT_LABELS.changeLabel}</span>
              </div>
            </> : <>
              <div className="price-card">
                <div className="price-label">WTI Crude</div>
                <div className="price-value">${formatPrice(marketPrices?.data?.current?.wti_price)}</div>
                {marketPrices?.data?.changes && formatPriceChange(marketPrices.data.changes.wti)}
              </div>
              <div className="price-card">
                <div className="price-label">Henry Hub</div>
                <div className="price-value">${formatPrice(marketPrices?.data?.current?.henry_hub_price)}</div>
                {marketPrices?.data?.changes && formatPriceChange(marketPrices.data.changes.henryHub)}
              </div>
              <div className="price-card">
                <div className="price-label">Midland Basis</div>
                <div className="price-value">${formatPrice(marketPrices?.data?.current?.midland_basis)}</div>
                <span className="price-sublabel">vs WTI</span>
              </div>
              <div className="price-card">
                <div className="price-label">Waha Basis</div>
                <div className="price-value">${formatPrice(marketPrices?.data?.current?.waha_basis)}</div>
                <span className="price-sublabel">vs HH</span>
              </div>
            </>}
          </div>
        </section>

        {/* Top Operators Section */}
        <section className="databricks-section">
          <h4>{TENANT_LABELS ? TENANT_LABELS.operatorsTitle : 'Top Operators by IP30'}</h4>
          <div className="operators-list">
            {operators.slice(0, 5).map((op, idx) => (
              <div key={idx} className="operator-row">
                <span className="operator-rank">{idx + 1}</span>
                <span className="operator-name">{op.operator}</span>
                {isNexusShape ? <>
                  <span className="operator-metric">
                    {TENANT_LABELS.operatorPrimary(op)}
                  </span>
                  <span className="operator-rigs" title={isEmissionsTenant ? 'Repair rate' : 'Recycling rate'}>
                    {TENANT_LABELS.operatorSecond(op, formatPrice)}
                  </span>
                </> : <>
                  <span className="operator-metric">
                    {formatNumber(op.avg_ip_30)} <small>BOE/d</small>
                  </span>
                  <span className="operator-rigs" title="Active Rigs">
                    🔧 {op.active_rigs}
                  </span>
                </>}
              </div>
            ))}
          </div>
        </section>

        {/* Volume/Production by County Section */}
        <section className="databricks-section">
          <h4>{TENANT_LABELS ? TENANT_LABELS.productionTitle : 'Production by County (30-day)'}</h4>
          <div className="production-table">
            <table>
              <thead>
                <tr>
                  <th>{TENANT_LABELS ? TENANT_LABELS.productionCols.county : 'County'}</th>
                  {isNexusShape ? <>
                    <th>{TENANT_LABELS.productionCols.total}</th>
                    <th>{TENANT_LABELS.productionCols.disposed}</th>
                    <th>{TENANT_LABELS.productionCols.recycled}</th>
                  </> : <>
                    <th>Oil (bbl)</th>
                    <th>Gas (MCF)</th>
                    <th>Wells</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {productionSummary.slice(0, 6).map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.county}</td>
                    {isNexusShape ? <>
                      <td>{formatNumber(row.total_volume_bbl)}</td>
                      <td>{formatNumber(row.total_disposal_bbl)}</td>
                      <td>{formatNumber(row.total_recycled_bbl)}</td>
                    </> : <>
                      <td>{formatNumber(row.total_oil_bbls)}</td>
                      <td>{formatNumber(row.total_gas_mcf)}</td>
                      <td>{row.well_count}</td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Data Source Info */}
        <div className="databricks-footer">
          <div className="data-source">
            <span className="databricks-logo">⚡</span>
            Powered by <strong>Databricks</strong>
            {status?.catalog && (
              <span className="catalog-info">
                {status.catalog}.{status.schema}
              </span>
            )}
          </div>
          {lastRefresh && (
            <div className="last-refresh">
              Updated: {lastRefresh.toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DatabricksPanel;
